import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import useFacility from "../../../utils/hooks/useFacility";
import { GET_SWIMMING_LESSONS_TABLE } from "../../api/apiRoutes";
import { SwimmingScheduleData } from "../../models/SwimmingScheduleData";

interface UseSwimmingDataProps {
  dateFrom: Date;
  dateTo: Date;
  names?: string;
  /** Pre-filter by exercise groups — from saleStep.exerciseGroupIds, format "0,1;0,2" */
  exerciseGroupIds?: string;
}

export default function useSwimmingData({ dateFrom, dateTo, names, exerciseGroupIds }: UseSwimmingDataProps) {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const isMountedRef = useRef(true);

  const [data, setData] = useState<SwimmingScheduleData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchLessons = async () => {
    if (!facilityIds || !isMountedRef.current) return;

    if (isMountedRef.current) {
      setLoading(true);
      setError(false);
    }

    const formattedDateFrom = format(dateFrom, "yyyy-MM-dd");
    const formattedDateTo = format(dateTo, "yyyy-MM-dd");
    const url = GET_SWIMMING_LESSONS_TABLE(
      facilityIds,
      formattedDateFrom,
      formattedDateTo,
      names,
      undefined,
      exerciseGroupIds,
    );
    const { result, isError } = await get(url);

    if (!isMountedRef.current) return;

    if (!isError && result) {
      setData(result as SwimmingScheduleData);
    } else {
      setError(true);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, facilityIds]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchLessons,
  };
}
